import { refContainsTarget } from "@prismicio/editor-support/React"
import { filter, slice } from "@prismicio/editor-support/Set"
import { type KeyboardEvent, type RefObject, useEffect, useState } from "react"

export interface FilterOption {
  id: string
  label: string
}

interface UseFilterFieldProps {
  selectedValues: Set<FilterOption>
  fixedSelectedValues: Set<FilterOption>
  onSelectedValuesChange: (selectedValues: Set<FilterOption>) => void
  setOpen: (open: boolean) => void
  fieldRef: RefObject<HTMLDivElement>
}

export function useFilterField(props: UseFilterFieldProps) {
  const {
    selectedValues,
    onSelectedValuesChange,
    setOpen: setFooterOpen,
    fieldRef,
    fixedSelectedValues
  } = props

  const [searchText, setSearchText] = useState("")

  const isFieldLocked = fixedSelectedValues.size > 0

  function deleteItemFromValue(valueToDelete: FilterOption | -1) {
    if (valueToDelete === -1)
      return onSelectedValuesChange(
        slice(selectedValues, 0, selectedValues.size - 1)
      )

    return onSelectedValuesChange(
      filter(selectedValues, (value) => value.id !== valueToDelete.id)
    )
  }

  function onBackspace() {
    if (searchText === "") deleteItemFromValue(-1)
  }

  useEffect(() => {
    function onDocumentMouseDown(event: MouseEvent) {
      if (isFieldLocked) return

      const isClickInside = refContainsTarget(fieldRef, event.target)

      setFooterOpen(isClickInside)

      if (!isClickInside) setSearchText("")
    }

    document.addEventListener("mousedown", onDocumentMouseDown)

    return () => {
      document.removeEventListener("mousedown", onDocumentMouseDown)
    }
  }, [setFooterOpen, fieldRef, isFieldLocked])

  function onKeyDown(evt: KeyboardEvent) {
    if (evt.code === "Escape") {
      setFooterOpen(false)
      setSearchText("")
    }
  }

  function getInputPlaceholder() {
    if (isFieldLocked) return ""

    const isEmpty = selectedValues.size + fixedSelectedValues.size === 0

    if (isEmpty) return "Empty"

    return "Type something..."
  }

  return {
    deleteItemFromValue,
    onBackspace,
    onKeyDown,
    searchText,
    setSearchText,
    isFieldLocked,
    inputPlaceholder: getInputPlaceholder()
  }
}
